'use client'

import { yupResolver } from '@hookform/resolvers/yup'
import { getTranslate } from 'legacy-src/utilities/i18n'
import Markdown from 'markdown-to-jsx'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import React, { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import useRegisterEmployeeEmail from '@/legacy-src/components/company/hooks/useRegisterEmployeeEmail'
import { verifyEmployeeCode } from '@/legacy-src/components/company/utils/benefitVerification'
import InputField from '@/legacy-src/components/formFields/InputField'
import Loader from '@/legacy-src/components/Loader'
import { Button, ButtonLink } from '@/legacy-src/components/ui/button'
import { useApis } from '@/legacy-src/contexts/api-context'
import { useSession } from '@/legacy-src/contexts/session-context'
import { useCompanyBenefitsActivationLinkValidity } from '@/legacy-src/hooks/useCompanyBenefitsActivationLinkValidity'
import useCompanyBenefitsConfirmation from '@/legacy-src/hooks/useCompanyBenefitsConfirmation'
import { useCompanyBenefitsSearchParams } from '@/legacy-src/hooks/useCompanyBenefitsSearchParams'
import { type StrapiCompanyPageType } from '@/legacy-src/lib/strapi/types'
import { cn } from '@/legacy-src/lib/utils'
import Container from '@/legacy-src/rebrand/Container'
import { type SiblingCompany } from '@/legacy-src/types/UserStateType'
import Toaster from '@/legacy-src/utilities/toaster'

import { CompanyPublicLanguageSelector } from './CompanyPublicLanguageSelector'

export interface CompanyPublicHeaderProps {
  slug: string
  logoUrl?: string
  companyName: string
  acceptCode: boolean
  acceptsEmail: boolean
  landingPageImageUrl?: string
  siblingCompanies?: SiblingCompany[]
  isHearmeMigratedCompany?: boolean
  cmsData: StrapiCompanyPageType
  classNames?: {
    container?: string
    logo?: string
    logoContainer?: string
    description?: string
    title?: string
    label?: string
    input?: string
    button?: string
    outlineButton?: string
    link?: string
  }
}

export const CompanyPublicHeader = ({
  slug,
  logoUrl,
  landingPageImageUrl,
  companyName,
  acceptCode,
  acceptsEmail,
  siblingCompanies,
  isHearmeMigratedCompany,
  classNames = {},
  cmsData,
}: CompanyPublicHeaderProps) => {
  const { title, customFormPrimaryCTA, customFormSecondaryCTA, customInputFieldLabel, customFormNote, customSubtitle } =
    cmsData

  const { t } = getTranslate()
  const router = useRouter()
  const { activationLinkId, blinkClientId } = useCompanyBenefitsSearchParams()
  const { api } = useApis()
  const { linkIsValid, isLoadingLinkValidity } = useCompanyBenefitsActivationLinkValidity(slug)
  const registerEmployeeEmail = useRegisterEmployeeEmail()
  const { isLoggedIn } = useSession()
  const { confirmCompany, loginAndConfirmCompany, registerAndConfirmCompany } = useCompanyBenefitsConfirmation(slug)

  const [isPending, setIsPending] = React.useState(false)

  const invalidActivationLink = activationLinkId && linkIsValid === false

  useEffect(() => {
    if (invalidActivationLink) {
      Toaster.error('component.benefits.errors.linkNotValid')
    }
  }, [invalidActivationLink])

  const formCodeOrEmail = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: { codeOrEmail: '' },
    resolver: yupResolver(
      Yup.object().shape({
        codeOrEmail: Yup.string().required('validationErrors.required'),
      }),
    ),
  })
  const formEmail = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: { email: '' },
    resolver: yupResolver(
      Yup.object().shape({
        email: Yup.string().email('validationErrors.email.badEmailFormat').required('validationErrors.required'),
      }),
    ),
  })

  const handleBenefit = async (values) => {
    setIsPending(true)

    if (values.codeOrEmail) {
      if (acceptsEmail && values.codeOrEmail.includes('@')) {
        await registerEmployeeEmail(values.codeOrEmail, slug)
      } else {
        const data = await verifyEmployeeCode(api, values.codeOrEmail, slug)
        if (data?.path) {
          router.push(data.path)
        }
      }
    } else {
      await registerEmployeeEmail(values.email, slug)
    }
    setIsPending(false)
  }

  const handleSubmitEmail = formEmail.handleSubmit(handleBenefit, (e) => {
    console.log(e)
  })

  const handleSubmitCodeOrEmail = formCodeOrEmail.handleSubmit(handleBenefit, (e) => {
    console.log(e)
  })

  return (
    <>
      <Container className={cn('container pb-6', classNames.container)}>
        <div className="w-full">
          <div className={cn('grid grid-cols-1', landingPageImageUrl ? 'tablet:grid-cols-12' : 'tablet:grid-cols-2')}>
            <div
              className={cn(
                'flex flex-col justify-center pt-12 tablet:mr-8 desktop:mr-20',
                landingPageImageUrl ? 'tablet:col-span-5' : '',
              )}
            >
              {logoUrl ? (
                <div className={cn('mb-4 max-w-[180px]', classNames.logoContainer)}>
                  <img src={logoUrl} alt={companyName} className={cn('object-contain', classNames.logo)} />
                </div>
              ) : null}
              <h2 className={cn('mb-6 text-5xl font-normal leading-none text-secondary', classNames.title)}>{title}</h2>

              <CompanyPublicLanguageSelector siblingCompanies={siblingCompanies} />

              <div className="form-group mb-6">
                {isLoadingLinkValidity ? (
                  <Loader visible loaderType="section-large" />
                ) : invalidActivationLink ? (
                  <div className="font-bold text-danger">
                    {t('component.benefits.errors.linkNotValid')} {t('component.benefits.errors.contactHR')}
                  </div>
                ) : (activationLinkId && linkIsValid) || blinkClientId ? (
                  <>
                    <div className={cn('mb-8 font-bold', classNames.description)}>
                      {customSubtitle ? (
                        <Markdown>{customSubtitle}</Markdown>
                      ) : (
                        t('component.company.activateLink.title', { company: companyName || slug })
                      )}
                    </div>

                    <div className="flex flex-wrap gap-4">
                      {isHearmeMigratedCompany ? (
                        <>
                          <Button
                            className={cn(classNames.button)}
                            size="medium"
                            onClick={isLoggedIn ? confirmCompany : loginAndConfirmCompany}
                          >
                            {t('component.company.activate.login')}
                          </Button>
                          <Button
                            className={cn(classNames.outlineButton)}
                            onClick={registerAndConfirmCompany}
                            size="medium"
                            variant="outlineSecondary"
                          >
                            {t('component.company.activate.register')}
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button className={cn(classNames.button)} size="medium" onClick={registerAndConfirmCompany}>
                            {t('component.company.activate.register')}
                          </Button>
                          <Button
                            className={cn(classNames.outlineButton)}
                            size="medium"
                            onClick={isLoggedIn ? confirmCompany : loginAndConfirmCompany}
                            variant="outlineSecondary"
                          >
                            {t('component.company.activate.login')}
                          </Button>
                        </>
                      )}
                    </div>
                    {(customFormNote || isHearmeMigratedCompany) && (
                      <p className="mt-6 text-base">
                        {customFormNote ? (
                          <Markdown>{customFormNote}</Markdown>
                        ) : (
                          t('component.company.hearmeActivationNote')
                        )}
                      </p>
                    )}
                  </>
                ) : acceptCode ? (
                  <FormProvider {...formCodeOrEmail}>
                    <form className="w-full" onSubmit={handleSubmitCodeOrEmail}>
                      <InputField
                        name="codeOrEmail"
                        label={
                          customInputFieldLabel ??
                          t(
                            acceptsEmail
                              ? 'component.company.benefitPersonalCodeOrEmailLabel'
                              : 'component.company.benefitPersonalCodeLabel',
                          )
                        }
                        required
                        className="max-w-[400px]"
                        inputClassName={classNames.input}
                        labelClassName={classNames.label}
                      />

                      <Button
                        loading={isPending}
                        onClick={handleSubmitCodeOrEmail}
                        className={cn('mt-1', classNames.button)}
                        size="medium"
                      >
                        {customFormPrimaryCTA ?? t('component.button.getBenefits')}
                      </Button>
                      <ButtonLink
                        href="#how-to-use-benefits"
                        variant="inline"
                        className={cn('mt-4 block text-sm', classNames.link)}
                      >
                        {customFormSecondaryCTA ?? t('component.button.useBenefits')}
                      </ButtonLink>
                    </form>
                  </FormProvider>
                ) : (
                  <FormProvider {...formEmail}>
                    <form className="mt-6 w-full" onSubmit={handleSubmitEmail}>
                      <InputField
                        placeholder="jana@amos.cz"
                        name="email"
                        label={t(`component.company.benefitCodeLabel`)}
                        required
                        inputClassName={classNames.input}
                        labelClassName={classNames.label}
                      />

                      <Button
                        loading={isPending}
                        onClick={handleSubmitEmail}
                        className={cn('mt-1', classNames.button)}
                        size="medium"
                      >
                        {customFormPrimaryCTA ?? t('component.button.getBenefits')}
                      </Button>
                      <a
                        href="#how-to-use-benefits"
                        className={cn(
                          'tablet mt-4 block justify-start px-6 py-3 text-sm font-bold uppercase',
                          classNames.link,
                        )}
                      >
                        {customFormSecondaryCTA ?? t('component.button.useBenefits')}
                      </a>
                    </form>
                  </FormProvider>
                )}
              </div>
            </div>
            {landingPageImageUrl ? (
              <div className="relative mx-auto my-auto aspect-square tablet:col-span-7 tablet:w-2/3">
                <Image
                  fill
                  sizes="570px"
                  alt={companyName}
                  src={landingPageImageUrl}
                  className="hidden object-contain tablet:block"
                />
              </div>
            ) : (
              <div className="flex items-center pb-6">
                <Image
                  sizes="570px"
                  height={413}
                  width={570}
                  alt=""
                  src={'/images/company-landing.png'}
                  className="mt-8 hidden tablet:block"
                />
              </div>
            )}
          </div>
        </div>
      </Container>
    </>
  )
}
